import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { tenantService } from '../services/tenantService';
import { appointmentService } from '../services/appointmentService';
import { createCommonStyles } from '../theme/styles';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { colors, getCalendarColors } from '../theme/colors';

// Lokalizacija za srpski jezik
moment.locale('sr', {
  months: [
    'Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun',
    'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'
  ],
  monthsShort: [
    'Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun',
    'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'
  ],
  weekdays: [
    'Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'
  ],
  weekdaysShort: ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'],
  weekdaysMin: ['Ne', 'Po', 'Ut', 'Sr', 'Če', 'Pe', 'Su']
});

const localizer = momentLocalizer(moment);

const AppointmentCalendar = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const { user: currentUser } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Defaultni prikaz - 'day' za mobilni, 'month' za desktop
  const [currentView, setCurrentView] = useState(isMobile ? 'day' : 'month');
  const { isDarkMode } = useAppTheme();
  const commonStyles = createCommonStyles(isDarkMode);
  const calendarColors = getCalendarColors(isDarkMode);

  const getCurrentTenantId = useCallback(() => {
      // Ako postoji tenantId direktno na user objektu (stari način)
      if (currentUser?.tenantId) {
        return currentUser.tenantId?._id || currentUser.tenantId;
      }
      
      // Ako postoji tenantConnections, uzmi tenantId iz prve konekcije
      if (currentUser?.tenantConnections && currentUser.tenantConnections.length > 0) {
        const firstConnection = currentUser.tenantConnections[0];
        return firstConnection?.tenantId?._id || firstConnection?.tenantId;
      }
      
      return null;
    }, [currentUser]);
    
    // Helper funkcija za čitanje userType iz tenantConnections na osnovu selectedTenantId
    const getUserTypeFromCurrentTenant = useCallback(() => {
      if (!currentUser?.tenantConnections || currentUser.tenantConnections.length === 0) {
        return currentUser?.userType;
      }
      
      // Ako postoji selectedTenantId, traži taj connection
      if (selectedTenantId) {
        const connection = currentUser.tenantConnections.find(
          tc => {
            const tcTenantId = tc.tenantId?._id || tc.tenantId;
            return tcTenantId === selectedTenantId;
          }
        );
        
        if (connection?.userType) {
          return connection.userType;
        }
      }
      
      // Falls back na prvi connection ili user.userType
      const firstConnection = currentUser.tenantConnections[0];
      return firstConnection?.userType || currentUser?.userType;
    }, [currentUser, selectedTenantId]);

  // Učitavanje tenanta
  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await tenantService.getAllTenants();
      setTenants(response.tenants || []);
    } catch (err) {
      console.error('Greška pri učitavanju tenanta:', err);
      setError(`Greška pri učitavanju tenanta: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Učitavanje termina
  const fetchAppointments = useCallback(async () => {
    try {
      setError(null);

      const actualUserType = getUserTypeFromCurrentTenant();
      
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const isCustomer = !actualUserType || actualUserType === "customer";
      const isEmployee = actualUserType === "employee";
      const tenantId = getCurrentTenantId();

      let response;
      if (isSuperAdmin) {
        // Za super admin, koristi selectedTenantId ili sve termine
        if (selectedTenantId) {
          response = await appointmentService.getAppointmentsByTenant(selectedTenantId);
        } else {
          response = await appointmentService.getAllAppointments();
        }
      } else if (isCustomer) {
        response = await appointmentService.getAppointmentsByCustomer(currentUser._id, 1, 99999, tenantId);
      } else if (isEmployee) {
        response = await appointmentService.getAppointmentsByEmployee(currentUser._id, 1, 99999);
      } else if (isTenantAdmin && tenantId) {
        response = await appointmentService.getAppointmentsByTenant(tenantId, 1, 99999);
      } else {
        setAppointments([]);
        return;
      }
      
      setAppointments(response.appointments || []);
    } catch (err) {
      console.error('Greška pri učitavanju termina:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(`Greška pri učitavanju termina: ${err.response?.data?.message || err.message}`);
    }
  }, [currentUser, selectedTenantId, getCurrentTenantId, getUserTypeFromCurrentTenant]);

  // Transformacija termina za kalendar - samo potvrđeni termini
  const calendarEvents = useMemo(() => {
    const actualUserType = getUserTypeFromCurrentTenant();
    const isCustomer = !actualUserType || actualUserType === "customer";

    return appointments
      .filter(appointment => appointment.status === 'confirmed')
      .map(appointment => {
        // Koristi selectedTime ako postoji, inače koristi vreme iz appointmentDate
        let startDate = new Date(appointment.appointmentDate);
        
        if (appointment.selectedTime) {
          // Parsiraj selectedTime (format "HH:MM")
          const [hours, minutes] = appointment.selectedTime.split(':').map(v => parseInt(v, 10));
          // Postavi datum bez vremena, zatim dodaj vreme iz selectedTime
          const dateOnly = new Date(appointment.appointmentDate);
          dateOnly.setHours(0, 0, 0, 0);
          startDate = new Date(dateOnly);
          startDate.setHours(hours || 0, minutes || 0, 0, 0);
        }
        
        // End date se računa na osnovu startDate (koji koristi selectedTime) plus duration
        const endDate = new Date(startDate.getTime() + (appointment.duration || 0) * 60000);
        
        // Logika za prikazivanje imena korisnika
        let customerDisplayName = 'N/A';
        if (appointment.isQuickAppointment && appointment.customerName) {
          // Za brze termine, koristi customerName
          customerDisplayName = appointment.customerName;
        } else if (appointment.customerId) {
          // Za obične termine, koristi customerId
          if (typeof appointment.customerId === 'object') {
            // Ako je customerId objekat
            customerDisplayName = `${appointment.customerId.firstName || ''} ${appointment.customerId.lastName || ''}`.trim();
          } else {
            // Ako je customerId string ID, koristi ime trenutno ulogovanog korisnika
            if (isCustomer && appointment.customerId === currentUser._id) {
              customerDisplayName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
            }
          }
        }
        
        return {
          id: appointment._id,
          title: customerDisplayName,
          start: startDate,
          end: endDate,
          resource: {
            appointment: appointment,
            services: appointment.serviceIds?.map(service => service.name).join(', ') || 'N/A',
            duration: appointment.duration,
            totalPrice: appointment.totalPrice || appointment.calculatedTotalPrice,
            tenant: appointment.tenantId?.name || 'N/A',
            status: appointment.status
          }
        };
      });
  }, [appointments, currentUser, getUserTypeFromCurrentTenant]);

  useEffect(() => {
    if (currentUser?.userType === 'super_admin') {
      fetchTenants();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  // Učitavanje termina kada se promeni tenant ili datum
  useEffect(() => {
    if (currentUser) {
      fetchAppointments();
    }
  }, [currentUser, selectedTenantId, currentDate, currentView, fetchAppointments]);

  // Kada se promeni isMobile, postavi odgovarajući view
  useEffect(() => {
    if (isMobile && currentView !== 'day') {
      setCurrentView('day');
    }
  }, [isMobile, currentView]);

  // Custom toolbar za bolje stilove - responsive
  const CustomToolbar = ({ label, onNavigate, onView, view }) => {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        p: 2,
        backgroundColor: colors.calendar.background.main,
        borderRadius: 1,
        gap: isMobile ? 2 : 1,
        minHeight: isMobile ? 'auto' : '60px'
      }}>
        {/* Navigacioni dugmići */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: isMobile ? 'center' : 'flex-start'
        }}>
          <button 
            onClick={() => onNavigate('PREV')}
            style={{
              background: colors.calendar.button.inactive.background,
              border: `1px solid ${colors.calendar.button.inactive.border}`,
              color: colors.calendar.button.inactive.color,
              padding: isMobile ? '6px 10px' : '8px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              minWidth: isMobile ? '80px' : '100px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = colors.calendar.button.hover.background;
              e.target.style.borderColor = colors.calendar.button.hover.border;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = colors.calendar.button.inactive.background;
              e.target.style.borderColor = colors.calendar.button.inactive.border;
            }}
          >
            ← Prethodni
          </button>
          <button 
            onClick={() => onNavigate('TODAY')}
            style={{
              background: colors.calendar.button.inactive.background,
              border: `1px solid ${colors.calendar.button.inactive.border}`,
              color: colors.calendar.button.inactive.color,
              padding: isMobile ? '6px 10px' : '8px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              minWidth: isMobile ? '80px' : '100px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = colors.calendar.button.hover.background;
              e.target.style.borderColor = colors.calendar.button.hover.border;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = colors.calendar.button.inactive.background;
              e.target.style.borderColor = colors.calendar.button.inactive.border;
            }}
          >
            Danas
          </button>
          <button 
            onClick={() => onNavigate('NEXT')}
            style={{
              background: colors.calendar.button.inactive.background,
              border: `1px solid ${colors.calendar.button.inactive.border}`,
              color: colors.calendar.button.inactive.color,
              padding: isMobile ? '6px 10px' : '8px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              minWidth: isMobile ? '80px' : '100px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = colors.calendar.button.hover.background;
              e.target.style.borderColor = colors.calendar.button.hover.border;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = colors.calendar.button.inactive.background;
              e.target.style.borderColor = colors.calendar.button.inactive.border;
            }}
          >
            Sledeći →
          </button>
        </Box>
        
        {/* Naslov */}
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          sx={{
            ...commonStyles.textPrimary,
            textAlign: 'center',
            fontSize: isMobile ? '1.1rem' : '1.3rem',
            fontWeight: 'bold',
            minWidth: isMobile ? '100%' : '200px'
          }}
        >
          {label}
        </Typography>
        
        {/* View dugmići */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: isMobile ? 'center' : 'flex-end'
        }}>
          {/* Mesec dugme - samo na desktop-u */}
          {!isMobile && (
            <button 
              onClick={() => onView('month')}
              style={{
                background: view === 'month' ? colors.calendar.button.active.background : colors.calendar.button.inactive.background,
                border: `1px solid ${colors.calendar.button.inactive.border}`,
                color: colors.calendar.button.inactive.color,
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '0.9rem',
                minWidth: '80px'
              }}
              onMouseEnter={(e) => {
                if (view !== 'month') {
                  e.target.style.background = colors.calendar.button.hover.background;
                  e.target.style.borderColor = colors.calendar.button.hover.border;
                }
              }}
              onMouseLeave={(e) => {
                if (view !== 'month') {
                  e.target.style.background = colors.calendar.button.inactive.background;
                  e.target.style.borderColor = colors.calendar.button.inactive.border;
                }
              }}
            >
              Mesec
            </button>
          )}
          {/* Nedelja dugme - samo na desktop-u */}
          {!isMobile && (
            <button 
              onClick={() => onView('week')}
              style={{
                background: view === 'week' ? colors.calendar.button.active.background : colors.calendar.button.inactive.background,
                border: `1px solid ${colors.calendar.button.inactive.border}`,
                color: colors.calendar.button.inactive.color,
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '0.9rem',
                minWidth: '80px'
              }}
              onMouseEnter={(e) => {
                if (view !== 'week') {
                  e.target.style.background = colors.calendar.button.hover.background;
                  e.target.style.borderColor = colors.calendar.button.hover.border;
                }
              }}
              onMouseLeave={(e) => {
                if (view !== 'week') {
                  e.target.style.background = colors.calendar.button.inactive.background;
                  e.target.style.borderColor = colors.calendar.button.inactive.border;
                }
              }}
            >
              Nedelja
            </button>
          )}
          <button 
            onClick={() => onView('day')}
            style={{
              background: view === 'day' ? colors.calendar.button.active.background : colors.calendar.button.inactive.background,
              border: `1px solid ${colors.calendar.button.inactive.border}`,
              color: colors.calendar.button.inactive.color,
              padding: isMobile ? '6px 8px' : '8px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: isMobile ? '0.7rem' : '0.9rem',
              minWidth: isMobile ? '50px' : '80px'
            }}
            onMouseEnter={(e) => {
              if (view !== 'day') {
                e.target.style.background = colors.calendar.button.hover.background;
                e.target.style.borderColor = colors.calendar.button.hover.border;
              }
            }}
            onMouseLeave={(e) => {
              if (view !== 'day') {
                e.target.style.background = colors.calendar.button.inactive.background;
                e.target.style.borderColor = colors.calendar.button.inactive.border;
              }
            }}
          >
            Dan
          </button>
        </Box>
      </Box>
    );
  };

  // Custom event component za bolje stilove
  const EventComponent = ({ event }) => {
    return (
      <Box
        sx={{
          height: '100%',
          padding: '2px 4px',
          fontSize: '12px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          backgroundColor: colors.calendar.background.header,
          color: colors.calendar.text.primary,
          borderRadius: '2px',
          border: `1px solid ${colors.calendar.border.strong}`,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: colors.calendar.background.selected,
            borderColor: colors.calendar.border.hover
          }
        }}
      >
        {event.title}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={commonStyles.textPrimary}>Učitavanje...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: 'error.main' }}>{error}</Typography>
      </Box>
    );
  }

  return (
   
      <div>
    
        
        {/* Tenant dropdown */}
        {currentUser?.userType === 'super_admin' && (
          <Box sx={{ mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel sx={commonStyles.textSecondary}>Izaberi Tenant</InputLabel>
              <Select
                value={selectedTenantId}
                label="Izaberi Tenant"
                onChange={(e) => {
                  console.log('Tenant selected:', e.target.value);
                  setSelectedTenantId(e.target.value);
                }}
                sx={commonStyles.selectField}
              >
                <MenuItem value="">
                  <em>Svi tenanti</em>
                </MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant._id} value={tenant._id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        
        {/* Prikazujemo statistiku samo za super_admin */}
        {currentUser?.userType === 'super_admin' && (
          <Typography variant="body2" sx={commonStyles.textSecondary}>
            Prikazuje se {calendarEvents.length} potvrđenih termina od {appointments.length} ukupno za {selectedTenantId ? tenants.find(t => t._id === selectedTenantId)?.name || 'izabrani tenant' : 'sve tenante'}
          </Typography>
        )}
 

      {/* Kalendar  */}
      <Box sx={{ 
        height: isMobile ? 'calc(100vh - 150px)' : '600px',
        '& .rbc-calendar': {
          backgroundColor: 'transparent',
          color: calendarColors.text.primary,
          border: `1px solid ${calendarColors.border.medium}`
        },
        '& .rbc-header': {
          backgroundColor: calendarColors.background.main,
          color: calendarColors.text.primary,
          borderBottom: `1px solid ${calendarColors.border.medium}`,
          padding: '8px 4px'
        },
        '& .rbc-month-view, & .rbc-time-view': {
          backgroundColor: calendarColors.background.cell,
          border: `1px solid ${calendarColors.border.medium}`
        },
        '& .rbc-date-cell': {
          color: calendarColors.text.primary,
          borderRight: `1px solid ${calendarColors.border.medium}`,
          borderBottom: `1px solid ${calendarColors.border.medium}`
        },
        '& .rbc-off-range-bg': {
          backgroundColor: calendarColors.background.cell
        },
        '& .rbc-today': {
          backgroundColor: calendarColors.background.header
        },
        '& .rbc-time-slot': {
          borderTop: `1px solid ${calendarColors.border.medium}`
        },
        '& .rbc-time-header-content': {
          borderLeft: `1px solid ${calendarColors.border.medium}`
        },
        // Event stilovi
        '& .rbc-event': {
          backgroundColor: 'transparent !important',
          border: 'none !important'
        },
        '& .rbc-event-content': {
          backgroundColor: 'transparent !important'
        },
        // Linije za month view
        '& .rbc-month-view .rbc-date-cell': {
          borderRight: `1px solid ${calendarColors.border.medium}`,
          borderBottom: `1px solid ${calendarColors.border.medium}`
        },
        '& .rbc-month-view .rbc-date-cell:last-child': {
          borderRight: 'none'
        },
        '& .rbc-month-view .rbc-row:last-child .rbc-date-cell': {
          borderBottom: 'none'
        },
        // Linije za time view (week/day)
        '& .rbc-time-view .rbc-time-content > * + * > *': {
          borderLeft: `1px solid ${calendarColors.border.medium}`
        },
        '& .rbc-time-view .rbc-day-slot': {
          borderRight: `1px solid ${calendarColors.border.medium}`
        },
        '& .rbc-time-view .rbc-day-slot:last-child': {
          borderRight: 'none'
        },
        '& .rbc-time-view .rbc-timeslot-group': {
          borderBottom: `1px solid ${calendarColors.border.medium}`
        },
        // Responsive stilovi za mobilne uređaje
        ...(isMobile && {
          '& .rbc-toolbar': {
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '16px'
          },
          '& .rbc-toolbar-label': {
            fontSize: '1.1rem',
            margin: '8px 0'
          },
          '& .rbc-btn-group': {
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap'
          },
          '& .rbc-btn-group button': {
            padding: '6px 12px',
            fontSize: '0.8rem',
            minWidth: '60px'
          },
          '& .rbc-month-view .rbc-date-cell': {
            minHeight: '60px',
            padding: '2px'
          },
          '& .rbc-day-view .rbc-time-slot': {
            minHeight: '40px'
          },
          '& .rbc-header': {
            padding: '6px 2px',
            fontSize: '0.8rem'
          },
          '& .rbc-event': {
            fontSize: '0.7rem',
            padding: '1px 2px'
          }
        })
      }}>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          views={isMobile ? ['day'] : ['month', 'week', 'day']} // Na mobilnom samo day
          view={currentView} // Kontroliši trenutni view
          defaultView={isMobile ? 'day' : 'month'} // Default view
          step={isMobile ? 60 : 30} // Veći step na mobilnim (60 min umesto 30)
          timeslots={isMobile ? 1 : 2} // Manje timeslots na mobilnim
          culture="sr"
          onNavigate={(date) => {
            console.log('Calendar navigated to:', date);
            setCurrentDate(date);
          }}
          onView={(view) => {
            console.log('Calendar view changed to:', view);
            setCurrentView(view);
          }}
          components={{
            toolbar: CustomToolbar,
            event: EventComponent
          }}
        />
      </Box>
      </div>
  );
};

export default AppointmentCalendar;